import { useState, useEffect } from 'react'
import { Col } from 'antd'
import { Icon, Popover, ProposalBlock, Tip } from '@vzmi/shp_shared_compo'
import { PROPOSAL_PHASE_TYPE, PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import _ from 'lodash'
import { HyperLink } from '../../../Common/Form'
import { getLayerModelOptions } from '../../../BatchProposal/UpdateAttribute/helper'
import { getDuplicatedSkuInSpecsList } from '../../../BatchProposal/Common/PrefixBlock/Duplicated'
import withProposalListingFormUploadFiles from '../../Common/HOC/withProposalListingFormUploadFiles'
import ImageFunctionTable from '../../UpdateListing/ImageFunctionTable/ImageFunctionTable'
import { IconPopoverLabel } from '../../Common/ProposalForm/FormLabelTooltip/FormLabelTooltip'
import { getListingWithMergedModels } from '../helper'
import CustomImageCols from './CustomImageCols'
import ListingDescSection from './ListingDescSection'
import ListingNameSection from './ListingNameSection'
import UpdateFirstLayerModal from './UpdateFirstLayerModal'
import styles from './index.css'

export const getModelsWithProductName = (listing, newListingName, isTwoLayerListing) => {
  const {
    spreadModels = [],
    isNoModelsListing
  } = listing

  const newSpreadModels = _.map(spreadModels, model => {
    const layerOneSpecName = _.get(model, 'spec.selectedValue')

    // 無屬性賣場商品名稱 = 賣場名稱
    let productName = newListingName

    // 單層屬性賣場商品名稱 = 賣場名稱-第一層
    if (!isNoModelsListing) {
      productName = `${newListingName}-${layerOneSpecName}`
    }

    // 雙單層屬性賣場商品名稱 = 賣場名稱-第一層-第二層
    if (isTwoLayerListing) {
      const layerTwoSpecName = _.get(model, 'items[0].spec.selectedValue')
      productName = `${productName}-${layerTwoSpecName}`
    }

    return {
      ...model,
      productName
    }
  })

  return newSpreadModels
}

export const getIsTwoLayerListingFromSpreadModels = spreadModels => {
  const isTwoLayerListing = !_.isEmpty(_.get(spreadModels, '0.items[0]'))
  return isTwoLayerListing
}

export const MAX_PRODUCT_NAME_SIZE = 100
export const getCountLimitMapByLayer = (spreadModels, listingName) => {
  const isTwoLayerListing = getIsTwoLayerListingFromSpreadModels(spreadModels)
  // 長度計算會扣掉 `-`
  const offsetSize = isTwoLayerListing ? 2 : 1
  const countLimit = MAX_PRODUCT_NAME_SIZE - offsetSize
  const listingNameSize = _.size(listingName)
  const layerOne = _.flow(
    () => _.groupBy(spreadModels, 'sku'),
    groupedSpreadModels => _.reduce(groupedSpreadModels, (map, models = [], sku) => {
      const maxItemSpecSelectedValueSize = _.flow(
        () => _.concat(..._.map(models, 'items')),
        items => _.map(items, 'spec.selectedValue'),
        // 雙層賣場第一層會扣掉最長的第二層屬性名稱
        itemSpecSelectedValues => _.maxBy(itemSpecSelectedValues, _.size),
        _.size
      )()
      map[sku] = countLimit - maxItemSpecSelectedValueSize - listingNameSize
      return map
    }, {})
  )()
  const layerTwo = isTwoLayerListing
    ? _.reduce(spreadModels, (map, spreadModel = {}) => {
      const {
        spec,
        items = []
      } = spreadModel
      const itemModelSku = _.get(items, '0.sku')
      const parentSpecSelectedValueSize = _.size(_.get(spec, 'selectedValue', ''))
      map[itemModelSku] = countLimit - parentSpecSelectedValueSize - listingNameSize
      return map
    }, {})
    : {}
  return { layerOne, layerTwo }
}

const SpecHeaderWithPopupByLayer = props => {
  const {
    withPopup,
    children
  } = props
  if (!withPopup) {
    return children
  }

  return (
    <IconPopoverLabel
      align='middle'
      popoverContent={(
        <p>若調整將自動連動商品名稱修改</p>
      )}
      popoverPlacement='bottom'
    >
      {children}
    </IconPopoverLabel>
  )
}

const UpdateImageTable = props => {
  const {
    readOnly,
    listing = {},
    isTwoLayerListing,
    openUpdateFirstLayerModal,
    onFieldChange,
    layerOneCountLimitMap,
    layerTwoCountLimitMap
  } = props
  const {
    id: listingId,
    models = [],
    spreadModels = [],
    isNoModelsListing,
    shareMediaBetweenModels
  } = listing
  const duplicatedSkuInSpecsList = getDuplicatedSkuInSpecsList(models)
  const isDuplicatedSkuInSpecsListExist = !_.isEmpty(duplicatedSkuInSpecsList)
  const modelSpecHeader = _.get(models, '[0].spec.name', '')
  const itemSpecHeader = _.get(models, '[0].items[0].spec.name', '')

  return (
    <ImageFunctionTable
      readOnly={readOnly || isDuplicatedSkuInSpecsListExist}
      models={spreadModels}
      isNoModelsListing={isNoModelsListing}
      modelsFieldName='spreadModels'
      shareImageFieldName='shareMediaBetweenModels'
      syncImageFieldName='syncProductImages'
      modelSpecHeader={modelSpecHeader}
      itemSpecHeader={itemSpecHeader}
      shareMediaBetweenModels={shareMediaBetweenModels}
      defaultShowUploader={false}
      customColHeaders={(
        <>
          {!isNoModelsListing && (
            <Col
              span={6}
              key='specHeaderCustomHeader'
            >
              <div className={styles.customColHeader}>
                <SpecHeaderWithPopupByLayer
                  withPopup={!isTwoLayerListing}
                >
                  {!isTwoLayerListing && modelSpecHeader}
                  {/* 只有雙層屬性賣場修改頁才能批次修改第一層的屬性名稱 */}
                  {(isTwoLayerListing && !readOnly) && (
                    <Popover
                      content='點擊可修改賣場屬性項目顯示名稱'
                      onClick={openUpdateFirstLayerModal}
                    >
                      {modelSpecHeader}
                      {' '}
                      <Icon
                        name='compose'
                        color='rgba(0, 0, 0, 0.65)'
                        align='initial'
                        size={14}
                      />
                    </Popover>
                  )}
                </SpecHeaderWithPopupByLayer>
                {isTwoLayerListing && (
                  <SpecHeaderWithPopupByLayer withPopup>
                    {itemSpecHeader}
                  </SpecHeaderWithPopupByLayer>
                )}
              </div>
            </Col>
          )}
          <Col
            span={isNoModelsListing ? 16 : 10}
            key='productNameAndPartNoCustomHeader'
          >
            <div className={styles.customColHeader}>
              <IconPopoverLabel
                align='middle'
                popoverContent={(
                  <>
                    修改商品名稱不會連動賣場屬性項目顯示，您可直接修改項目顯示名稱或至
                    <HyperLink
                      href={`../${PROPOSAL_TYPE.UPDATE_LISTING_MODEL}/${PROPOSAL_PHASE_TYPE.COMPOSE}?listingId=${listingId}`}
                      target='_blank'
                    >
                      調整賣場屬性項目
                    </HyperLink>
                  </>
                )}
              >
                商品名稱
              </IconPopoverLabel>
              {!isNoModelsListing && (
                <IconPopoverLabel
                  align='middle'
                  popoverContent={(
                    <>
                      同MPN(製造商零件編號)
                      <br />
                      訂單成立時, 只會帶入各屬性項目所輸入的供應商商品料號
                    </>
                  )}
                  popoverPlacement='bottom'
                  suffix={(
                    <Tip
                      type='info'
                      tag='span'
                    >
                      <span className={styles.partNoTitleSuffix}>(非必填)</span>
                    </Tip>
                  )}
                  withSuffix
                >
                  供應商商品料號
                </IconPopoverLabel>
              )}
            </div>
          </Col>
        </>
      )}
      customCols={props => CustomImageCols({
        ...props,
        onFieldChange,
        layerOneCountLimitMap,
        layerTwoCountLimitMap
      })}
      syncProductImages
      hasDistinctSizesUploader
      withCustomCols
    />
  )
}

const UpdateImageTableWithHOC = withProposalListingFormUploadFiles(UpdateImageTable)

const UpdateImage = props => {
  const {
    supplier,
    change,
    readOnly,
    listing = {}
  } = props
  const {
    models = [],
    spreadModels = [],
    name: listingName
  } = listing
  const [layerOneCountLimitMap, setLayerOneCountLimitMap] = useState({})
  const [layerTwoCountLimitMap, setLayerTwoCountLimitMap] = useState({})
  const [updateFirstLayerModalVisible, setUpdateFirstLayerModalVisible] = useState(false)
  const itemSpecHeader = _.get(models, '[0].items[0].spec.name', '')
  const isTwoLayerListing = !_.isEmpty(itemSpecHeader)
  const updateProductName = newListing => {
    const { spreadModels, name } = newListing
    const modelsWithProductName = getModelsWithProductName(
      newListing,
      name,
      isTwoLayerListing
    )
    if (_.isEqual(spreadModels, modelsWithProductName)) {
      return
    }

    change('spreadModels', modelsWithProductName)
  }

  const updateLayerModels = newListing => {
    // 從 spreadModels 重組出最新的 models
    const { models } = getListingWithMergedModels(newListing)
    // 從 models 中重組出第一二層各自在修改規格表的商品屬性資料
    const [layerOneModel, layerTwoModel] = getLayerModelOptions(models)
    _.forIn({ layerOneModel, layerTwoModel }, (layerModel, key) => {
      const originLayerModel = _.get(newListing, key)
      // 屬性資訊不存在或修改前後一致不變更
      if (_.isEmpty(layerModel) || _.isEqual(originLayerModel, layerModel)) {
        return
      }

      // 更新商品屬性
      change(key, layerModel)
    })
  }

  // 欄位有變更時更新商品名稱(賣場名稱、第一、第二層屬性)
  const onEffectProductNameFieldChange = (e, newValue, oldValue, fieldName = '') => {
    const newListing = _.cloneDeep(listing)
    _.set(newListing, fieldName, newValue)
    updateProductName(newListing)

    // 修改第一二層屬性時同步更新規格表的商品屬性
    if (fieldName.startsWith('spreadModels')) {
      updateLayerModels(newListing)
    }
  }

  const onUpdateFirstLayerModalOk = ({ models: newModels = [] }) => {
    const modelBySku = _.keyBy(newModels, 'sku')
    const newSpreadModels = _.map(spreadModels, spreadModel => {
      const { sku, spec = {} } = spreadModel
      const selectedValue = _.get(modelBySku, `${sku}.spec.selectedValue`, {})
      return {
        ...spreadModel,
        spec: {
          ...spec,
          selectedValue
        }
      }
    })
    const newListing = {
      ...listing,
      spreadModels: newSpreadModels
    }
    // 批次修改雙層賣場的第一層屬性後更新商品名稱
    updateProductName(newListing)
    // 批次修改雙層賣場的第一層屬性後更新修改規格表的商品屬性
    updateLayerModels(newListing)
    // 更新雙層屬性賣場批次修改第一層屬性名稱的欄位
    change('models', newModels)
    setUpdateFirstLayerModalVisible(false)
  }

  useEffect(() => {
    const {
      layerOne = {},
      layerTwo = {}
    } = getCountLimitMapByLayer(spreadModels, listingName)
    setLayerOneCountLimitMap(layerOne)
    setLayerTwoCountLimitMap(layerTwo)
  }, [listingName, spreadModels, isTwoLayerListing])

  return (
    <>
      {/* 賣場編號與名稱 */}
      <ProposalBlock
        className={styles.updateImageBlock}
        data-role='listing-name-section'
      >
        <ListingNameSection
          onListingNameChange={onEffectProductNameFieldChange}
          supplier={supplier}
          readOnly={readOnly}
        />
      </ProposalBlock>
      {/* 修改圖片 */}
      <ProposalBlock
        className={styles.updateImageBlock}
        data-role='models-table-section'
      >
        <UpdateImageTableWithHOC
          isTwoLayerListing={isTwoLayerListing}
          openUpdateFirstLayerModal={() => setUpdateFirstLayerModalVisible(true)}
          onFieldChange={onEffectProductNameFieldChange}
          layerOneCountLimitMap={layerOneCountLimitMap}
          layerTwoCountLimitMap={layerTwoCountLimitMap}
          {...props}
        />
      </ProposalBlock>
      {/* 特色標題、商品特色、供應商商品料號、商品型號 */}
      <ProposalBlock
        data-role='sub-section'
      >
        <ListingDescSection
          readOnly={readOnly}
        />
      </ProposalBlock>
      <UpdateFirstLayerModal
        visible={updateFirstLayerModalVisible}
        onOk={onUpdateFirstLayerModalOk}
        onCancel={() => setUpdateFirstLayerModalVisible(false)}
        models={models}
        countLimitMap={layerOneCountLimitMap}
      />
    </>
  )
}

export default UpdateImage
